// The funder workspace tab. Mirrors `WorkspaceTab` from the funder-workspace
// types, but declared locally on purpose: this file lives under
// `src/client/utils`, which is in scope for the strict
// `noUncheckedIndexedAccess` typecheck. Importing the page-level types module
// here drags its (heavy) client component import graph into that strict
// program and surfaces hundreds of unrelated latent errors — and a generic
// util shouldn't depend on a page's types anyway.
export type WorkspaceTab = "review" | "decide";

// Per-round, per-tab memory of the last funder sub-path (the segment after
// `/funder/`, e.g. "review/proposals/p-123"). The funder workspace's
// Review/Decide tabs now live in the top SiteHeader as Review/Decide nav
// links; without this, switching tabs there would always reset to each side's
// landing page. We persist in sessionStorage (scoped to the browser tab) rather
// than the in-page ref so the SiteHeader — which is mounted outside the funder
// page — can read it, and so the place survives reloads within the session.

const DEFAULT_SUFFIX: Record<WorkspaceTab, string> = {
  review: "review/overview",
  decide: "decide/incremental-evals",
};

const keyFor = (roundId: string, tab: WorkspaceTab) =>
  `sprocess:funder:last:${roundId}:${tab}`;

export function rememberFunderTabPath(
  roundId: string,
  tab: WorkspaceTab,
  suffix: string,
): void {
  try {
    sessionStorage.setItem(keyFor(roundId, tab), suffix);
  } catch {
    // sessionStorage can throw (private mode, disabled storage). Memory is a
    // nicety, not load-bearing — fall back to the default landing on read.
  }
}

export function getFunderTabPath(roundId: string, tab: WorkspaceTab): string {
  try {
    return sessionStorage.getItem(keyFor(roundId, tab)) ?? DEFAULT_SUFFIX[tab];
  } catch {
    return DEFAULT_SUFFIX[tab];
  }
}
